import { Link } from 'react-router-dom';

import { AnchorHeading } from '../components/AnchorHeading';
import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';
import { useDocsPage } from '../hooks/useDocsPage';

export function SupportKnowledgeGuide() {
  useDocsPage('Build the knowledge base the AI answers from — Revvly Docs');
  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Build the knowledge base</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Build the <em>knowledge base</em> the AI answers from.
        </h1>
        <p className="dp-oneliner">
          The support AI answers only from the articles you give it, and shows each customer only
          the articles their role and plan may read. This guide covers the five ways to add
          articles, how to say who may read each one, and how to keep the whole thing healthy.
        </p>
      </header>

      <section className="dp-section">
        <AnchorHeading id="page">The Knowledge page</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge']}
          note="Every agent can read this page. Adding, changing and removing knowledge is for admins and owners; the buttons are hidden for everyone else."
        />
        <p className="dp-section-sub">
          The page has four parts: a banner when something needs attention, the pages waiting for a
          decision, the list of <strong>Sources</strong> (where articles come from), and the table
          of <strong>Articles</strong>, which shows each article&rsquo;s address and can be sorted,
          filtered and cleaned up in bulk (see <a href="#table">organise the articles</a>). A
          workspace can have at most ten sources. Removing a source deletes its articles, and the AI
          stops answering from them at once; the site or help centre itself is not touched.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="which">Which source should I use</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Your help is a public website</div>
            <div className="dp-field-v">
              Add a help site. Revvly reads the pages. It has to guess who each page is for, so some
              pages will wait for your decision.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">You have a few pages of text</div>
            <div className="dp-field-v">
              Add a document: paste the text and say who may read it. You decide the audience, so
              nothing is guessed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Your help is in Intercom</div>
            <div className="dp-field-v">
              Import, Help centre articles, Intercom. Revvly reads the published articles with your
              token, with no website to crawl.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Your help is in Zendesk</div>
            <div className="dp-field-v">
              Import, Help centre articles, Zendesk. Zendesk says who may read each article, and
              Revvly follows it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Your articles are in your own system</div>
            <div className="dp-field-v">
              Push them through the API. Your server states the audience for every article. See{' '}
              <Link to="/docs/guides/support-knowledge-api">Push your help articles</Link>.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="crawl">Crawl a help site</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Add help site']}
          note="Admins and owners. The site is read straight away once you add it."
        />
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Name</div>
            <div className="dp-field-v">How the source is listed, for example Help centre.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Address</div>
            <div className="dp-field-v">
              The full address, starting with https://. Only pages on this site are read, following
              its links and its sitemap.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Leave out</div>
            <div className="dp-field-v">
              Paths not to read, one per line, where an asterisk matches anything, such as{' '}
              <span className="api-inline">/blog/*</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Read it again</div>
            <div className="dp-field-v">
              Only when I ask, Every day or Every week. See <a href="#schedule">re-reading</a>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Ownership</div>
            <div className="dp-field-v">
              You must tick that the site is yours or your company&rsquo;s. Nothing is read until
              you do.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          Revvly reads your site politely: https only, it follows the site&rsquo;s robots.txt and
          crawl delay, skips pages marked noindex, and stops at a page limit (up to 2,000 pages; the
          source shows &ldquo;Stopped at the page limit&rdquo; when it did). It reads public pages
          only and needs no login or token. The schedule is chosen when you add the site; the page
          does not yet let you change it afterward. The paths to leave out can be changed at any
          time with <strong>Pages to leave out</strong> on the source (see{' '}
          <a href="#rules">keep a section out</a>).
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="upload">Add a document</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Add document']}
          note="Admins and owners. Documents are kept together in one source."
        />
        <p className="dp-section-sub">
          Give it a title, paste the text (Markdown or plain text, up to 200,000 characters), and
          choose <strong>Who can read it</strong>. Leave that empty to show it to everyone. The
          roles you choose are final from the start. You paste text; uploading a file such as a PDF
          or a Word document is not offered.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="intercom">Import an Intercom help centre</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Import', 'Help centre articles', 'Intercom']}
          note="Admins and owners. The same Import menu is at the foot of the inbox views column."
        />
        <p className="dp-section-sub">
          Choose Intercom, give the source a name, choose where your Intercom workspace is hosted
          (United States, Europe or Australia) and paste an Intercom access token that can read
          articles. Revvly checks the token before saving it, then reads your published articles.
          Drafts count as not there.
        </p>
        <p className="dp-section-sub">
          The Import menu has a second choice, <strong>Past conversations</strong>, which brings in
          your Intercom conversations as tickets with their history rather than articles. It opens
          the same conversation import described in{' '}
          <Link to="/docs/guides/support-setup#import-intercom">
            Import your Intercom conversations
          </Link>
          .
        </p>
        <p className="dp-section-sub">
          Intercom has no idea of who an article is for, so imported articles go through the same
          role matching as a crawled page, and an article with no public address is held for your
          review in case it is internal.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="zendesk">Import a Zendesk help centre</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Import', 'Help centre articles', 'Zendesk']}
          note="Admins and owners."
        />
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Zendesk address</div>
            <div className="dp-field-v">
              The first part of your address: <span className="api-inline">acme</span> for
              acme.zendesk.com.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Agent email</div>
            <div className="dp-field-v">The email of the Zendesk agent the token belongs to.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Language</div>
            <div className="dp-field-v">
              Optional, such as <span className="api-inline">en-us</span>. Empty reads your default
              language. One language is read per source.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">API token</div>
            <div className="dp-field-v">
              A Zendesk API token. API access must be switched on in Zendesk.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          Zendesk says who may read each article. An article shown to everyone is imported for
          everyone. One limited to a user segment waits in the review queue for you to choose roles,
          and is not used until you do. If someone restricts an article in Zendesk later, the next
          read pulls it out of use until you decide. The same Zendesk help centre and language can
          be connected once per workspace.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            Both imports only read. Revvly never creates, edits or deletes anything in Intercom or
            Zendesk. The token is stored encrypted and is not shown again.
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="api">Push through the API</AnchorHeading>
        <p className="dp-section-sub">
          An API source is filled by your own server, so there is nothing to add on this page. It
          appears in the Sources list as an API source once your server pushes its first article,
          with no Read now button and no schedule, because Revvly only changes it when your server
          calls the API. The full steps, with the key you need, are in{' '}
          <Link to="/docs/guides/support-knowledge-api">Push your help articles to Revvly</Link>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="audience">Who may read an article</AnchorHeading>
        <p className="dp-section-sub">
          Each article has an audience: <strong>everyone</strong>, or one or more{' '}
          <strong>roles</strong>, and optionally some <strong>plans</strong>. The roles are the ones
          you define with the <strong>Roles</strong> button on the Knowledge page. Your system tells
          Revvly each customer&rsquo;s role value, attributes and plan when they sign in to the chat
          (see <Link to="/docs/guides/support-tokens">Sign users in to the support chat</Link>), and
          each role&rsquo;s rules decide whether the customer has it. A customer can have several
          roles and sees articles open to any of them, plus everything open to everyone. A customer
          who matches no role, or a visitor with no role, sees only what is open to everyone.
        </p>
        <Figure
          title="From source to AI answer"
          caption="An article reaches the AI only once its audience is settled, and a held article stays out until you decide."
        >
          <Flow
            steps={[
              {
                title: 'Add a source',
                text: 'Help site, document, Intercom, Zendesk or API push.',
              },
              {
                fork: [
                  {
                    title: 'Audience is stated',
                    text: 'Documents, Zendesk and API articles say who may read them.',
                  },
                  {
                    title: 'Audience is matched',
                    text: 'Help sites and Intercom are compared with your role hints.',
                  },
                ],
              },
              {
                fork: [
                  {
                    title: 'Searchable',
                    text: 'Settled audience. The AI can use it.',
                    tone: 'accent',
                  },
                  {
                    title: 'Needs review',
                    text: 'Held and not used until you choose roles, Everyone or Leave out.',
                    tone: 'warn',
                  },
                ],
              },
              {
                title: 'AI answers',
                text: 'Only from articles the customer’s role and plan may read.',
                tone: 'accent',
              },
            ]}
          />
        </Figure>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Audience rules</div>
            <div className="dp-field-v">
              Rules you set on a source decide first, by the article&rsquo;s address. See{' '}
              <a href="#audience-rules">Set who reads a section by its address</a>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Placing a page by role</div>
            <div className="dp-field-v">
              When no rule matches, for crawled sites and Intercom, Revvly compares each article
              with the content hints of your roles (paths, section names and keywords you entered on
              each role). A confident match is used. If it cannot place an article, the article is
              held and not used.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Needs a decision</div>
            <div className="dp-field-v">
              Held articles are listed at the top of the page with the reasons Revvly found. For
              each you can choose <strong>Everyone</strong>, <strong>Choose roles</strong> or{' '}
              <strong>Leave out</strong>. Showing a role&rsquo;s page to everyone is the mistake
              that cannot be undone, which is why Revvly waits for you.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Changing it yourself</div>
            <div className="dp-field-v">
              In the Articles list, Who can read changes the roles of one article. Your choice is
              marked &ldquo;Set by an admin&rdquo; and later reads of the source update the
              article&rsquo;s text but never undo it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Plans</div>
            <div className="dp-field-v">
              Plans are set by whoever sends the article, for example a push through the API. The
              Who can read dialog changes roles and keeps whatever plans the article already has.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="status">Article and source status</AnchorHeading>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Searchable</div>
            <div className="dp-field-v">
              The AI can answer from it, for readers who may see it. This is the only status the AI
              uses.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Needs review</div>
            <div className="dp-field-v">
              Waiting for your decision on who may read it. Not used.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Left out</div>
            <div className="dp-field-v">You excluded it. Not used.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Failed</div>
            <div className="dp-field-v">
              It could not be read or updated. The reason is shown under its title. If an earlier
              version was already searchable, answers keep using that version until the next
              successful read.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          A source is <strong>Ready</strong>, <strong>Reading</strong> (the page refreshes its
          numbers every few seconds meanwhile), <strong>Failed</strong> or <strong>Off</strong>. The
          Articles column reads &ldquo;12 of 14&rdquo;: searchable out of all, with the number
          needing a decision and the number that could not be read underneath. The{' '}
          <strong>Used</strong> column counts how often the AI retrieved the article.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="table">Organise the articles</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Articles']}
          note="Every agent can read and filter the table. Selecting rows and the bulk actions are for admins and owners."
        />
        <p className="dp-section-sub">
          When a source brings in hundreds of pages, the Articles table is where you sort out what
          the AI should and should not answer from. The first column is the article&rsquo;s{' '}
          <strong>address</strong>, its path on your site, such as{' '}
          <span className="api-inline">/blog/some-post</span>. A pasted document has no address and
          says Document. Click a column heading to sort by Address, Title, Source, Status or
          Updated; click it again to reverse the order. The table shows 50 articles at a time with
          Previous and Next, and says how many articles match in all.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Search</div>
            <div className="dp-field-v">Matches the title or the full address.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Source, Status, Who can read</div>
            <div className="dp-field-v">
              Narrow the table to one source, one status, articles open to everyone, or articles one
              role may read. The Who can read column always shows, with each role as a badge, or
              Everyone.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Address rules</div>
            <div className="dp-field-v">
              Each rule is Starts with, Contains, Does not start with or Does not contain, followed
              by the text to match. <strong>Add address rule</strong> stacks up to 10 of them, and
              an article must meet all of them, so you can take a section and leave a part of it
              out: starts with <span className="api-inline">/docs</span> and does not contain{' '}
              <span className="api-inline">/fr</span>. The two &ldquo;does not&rdquo; options keep
              everything else.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Starts with</div>
            <div className="dp-field-v">
              Takes a section and everything below it: <span className="api-inline">/blog</span>{' '}
              finds <span className="api-inline">/blog</span> and{' '}
              <span className="api-inline">/blog/some-post</span>, but not{' '}
              <span className="api-inline">/blogger</span>. Capital letters, a trailing slash and a
              pasted full address do not matter.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Contains</div>
            <div className="dp-field-v">
              Finds the text anywhere in the address, for example{' '}
              <span className="api-inline">/comparison</span>. What you type is matched as plain
              text, never as a pattern.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Sections</div>
            <div className="dp-field-v">
              A row of buttons above the table lists the biggest first path segments with their
              counts, such as <span className="api-inline">/blog 212</span>. Click one to filter to
              that section; click it again to clear it. A section replaces only your starts-with
              rule and leaves the other address rules in place. The counts follow your other filters
              but not the address rules, so the other sections stay one click away.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="bulk">Change many articles at once</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Articles', 'Select rows']}
          note="Admins and owners."
        />
        <p className="dp-section-sub">
          Tick rows, or tick the box in the header to take the whole page. When the page is ticked
          and more articles match your filters, a line offers{' '}
          <strong>Select all 212 matching</strong>, which takes every article the current filters
          match, not just the page. A bar then shows how many are selected, with three actions:
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Leave out</div>
            <div className="dp-field-v">
              Takes the articles out of the AI&rsquo;s answers. They stay in the table as Left out,
              later reads of the site keep them out, and you can bring one back by choosing who can
              read it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who can read</div>
            <div className="dp-field-v">
              Sets the roles for all of them at once, as if you had set each by hand. Empty means
              everyone. Later reads of the site leave the decision alone.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Delete</div>
            <div className="dp-field-v">
              Deletes the articles for good and the AI stops answering from them. The confirmation
              states the exact count and what it applies to. A deleted page can come back the next
              time its source is read, so to keep a whole section out use Leave out, or{' '}
              <a href="#rules">pages to leave out</a>.
            </div>
          </div>
        </div>
        <p className="dp-section-sub">
          Every action sends the count you were shown. If the list changed in between (a read added
          pages, or someone else deleted some) and the real number is different, Revvly refuses and
          changes nothing, and tells you how many match now; look at the table again and repeat. One
          action takes at most 500 ticked rows, or 5,000 articles by filter; narrow the filters and
          repeat for more.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="audience-rules">Set who reads a section by its address</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Sources', 'Audience rules']}
          note="Admins and owners. For help sites, Intercom and Zendesk sources."
        />
        <p className="dp-section-sub">
          When your help site keeps each audience in its own folder, say so once instead of choosing
          roles article by article. Each rule has one or more conditions on the address path (starts
          with, ends with, contains, or the opposite of each; all conditions must hold) and the
          roles that may read what it matches, or Everyone. Rules run in order and the{' '}
          <strong>first rule that matches decides</strong>, so put the narrow ones on top: for
          example <span className="api-inline">/docs/user/applicant/</span> for Applicants above{' '}
          <span className="api-inline">/docs/</span> for Admins. Each rule shows how many pages it
          matches as you type.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Save</div>
            <div className="dp-field-v">
              The rules apply to pages as they are added or change, on the next read.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Save and apply to existing pages</div>
            <div className="dp-field-v">Also updates the pages already there, straight away.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">What a rule never changes</div>
            <div className="dp-field-v">
              A choice made by hand with Who can read (marked &ldquo;Set by an admin&rdquo;). A rule
              for Everyone also never opens a Zendesk article that Zendesk limits to a user segment;
              a rule that names roles can place it.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">No rule matches</div>
            <div className="dp-field-v">
              The article is placed from the roles&rsquo; content hints, as before. A folder written
              with a trailing slash, such as <span className="api-inline">/docs/applicant/</span>,
              matches that folder and everything in it, but not{' '}
              <span className="api-inline">/docs/applicant-faq</span>.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="rules">Keep a section out for good</AnchorHeading>
        <UiPath
          steps={['Support', 'Knowledge', 'Sources', 'Pages to leave out']}
          note="Admins and owners. For help sites, Intercom and Zendesk sources."
        />
        <p className="dp-section-sub">
          Imported a whole site and got the blog and comparison pages too? Add their paths as pages
          to leave out on the source, one per line, such as{' '}
          <span className="api-inline">/blog</span> and <span className="api-inline">/compare</span>
          . A rule matches every address that starts with it (an asterisk matches anything), so{' '}
          <span className="api-inline">/blog</span> also matches{' '}
          <span className="api-inline">/blogger</span>. Reads skip those pages, and the next read
          that finishes completely removes the ones already stored, so you do not have to delete
          them one by one. The rules are listed under the source&rsquo;s name. For Intercom and
          Zendesk the rule is matched against each article&rsquo;s public address, for example{' '}
          <span className="api-inline">/hc/en-us/articles/123</span>.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="schedule">Read now, or on a schedule</AnchorHeading>
        <p className="dp-section-sub">
          <strong>Read now</strong> on a source starts a read at once. It is offered for crawled
          sites and the two help centres, not for documents or API sources. A scheduled source
          (Every day, Every week) is read again when its last read is older than that; Revvly checks
          every few minutes. Only when I ask sources are read only when you press the button.
        </p>
        <p className="dp-section-sub">
          Before a read you start (Read now, or connecting Intercom or Zendesk), Revvly asks whether
          to <strong>Update who can read from the role hints</strong>. It is ticked by default.
          Ticked, articles whose readers Revvly guessed are guessed again from your roles&rsquo;
          current hints, even when their text did not change. Unticked, they keep the readers they
          have; new articles are still guessed. Articles whose readers you chose yourself are never
          changed either way, and neither are Zendesk&rsquo;s own user segments. Scheduled reads
          follow your last choice.
        </p>
        <p className="dp-section-sub">
          An article is removed only when a read that finished completely did not see it. A read
          that stopped at the page limit, failed or was interrupted proves nothing about the pages
          it did not reach, so it removes nothing.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="health">Keeping it healthy</AnchorHeading>
        <p className="dp-section-sub">
          When something needs attention, a red banner at the top of the Knowledge page says how
          many sources could not be read, how many articles could not be updated, how many of those
          still answer from an earlier version, and how many need a decision. The same total shows
          as a red badge on Knowledge in the sidebar, so you see it from any page. Nothing is sent
          by email or Slack yet; the badge is the alert.
        </p>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Automatic retry</div>
            <div className="dp-field-v">
              When a scheduled source fails, its row says &ldquo;Failed 3 times in a row. Trying
              again in 40 min.&rdquo; The wait starts at 5 minutes, doubles after each failure up to
              6 hours, and resets after a clean read. A source set to Only when I ask is not retried
              by itself.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Waiting for a new token</div>
            <div className="dp-field-v">
              An Intercom or Zendesk source whose token was refused (revoked, expired or lacking
              access) shows &ldquo;Waiting for a new token&rdquo; instead of a retry time. Revvly
              stops retrying, because trying again with the same token cannot help. The dashboard
              has no button yet to replace the token on an existing source. Until it does, remove
              the source and connect it again with a new token; the articles are read again, and
              audience decisions you made on them are lost.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Temporary trouble</div>
            <div className="dp-field-v">
              A brief outage or a rate limit at Intercom or Zendesk is retried within the read. If a
              read cannot be completed, nothing is removed on its strength.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="not-built">Not built yet</AnchorHeading>
        <ul className="dp-connects">
          <li>A box to test what a given role and plan would retrieve for a question.</li>
          <li>Changing a source&rsquo;s schedule after you add it.</li>
          <li>Reading an article again from the table for many rows at once.</li>
          <li>Replacing the token of an existing help centre source.</li>
          <li>Email or Slack alerts for failed sources.</li>
          <li>
            Uploading files (PDF, Word) or images; articles are text. Zendesk languages other than
            the one you choose, and Intercom collections, are not read.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link to="/docs/guides/support-knowledge-api">Push help articles to Support ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-ai">How the AI first line works ›</Link>
          </li>
          <li>
            <Link to="/docs/guides/support-setup">Set up Support for your team ›</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
